'use client';

import React, { useEffect, useState, useCallback } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import Topbar from '@/components/Topbar';
import { getScanHistory, deleteScan, ScanRecord } from '@/lib/scanHistoryService';
import {
  History, RefreshCw, Trash2, ExternalLink, TrendingDown, TrendingUp, Minus,
  AlertOctagon, AlertTriangle, CheckCircle2, Clock, Search, Loader2
} from 'lucide-react';

function getScoreColor(score: number): string {
  if (score >= 80) return 'var(--severity-pass)';
  if (score >= 60) return 'var(--severity-warning)';
  if (score >= 40) return '#FF8C3D';
  return 'var(--severity-critical)';
}

function getScoreLabel(score: number): string {
  if (score >= 80) return 'Low Friction';
  if (score >= 60) return 'Moderate';
  if (score >= 40) return 'High Friction';
  return 'Severe';
}

function ScoreIcon({ score }: { score: number }) {
  if (score >= 75) return <TrendingUp size={14} aria-hidden="true" />;
  if (score >= 50) return <Minus size={14} aria-hidden="true" />;
  return <TrendingDown size={14} aria-hidden="true" />;
}

function SeverityPills({ checks }: { checks: any[] }) {
  const critical = checks.filter((c: any) => c.severity === 'critical').length;
  const warning = checks.filter((c: any) => c.severity === 'warning').length;
  const pass = checks.filter((c: any) => c.severity === 'pass').length;

  return (
    <div className="flex items-center gap-1.5 flex-wrap">
      {critical > 0 && (
        <span className="flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded-md" style={{ background: 'var(--severity-critical-bg)', color: 'var(--severity-critical)', border: '1px solid rgba(255,61,87,0.2)' }}>
          <AlertOctagon size={10} aria-hidden="true" />
          {critical}
        </span>
      )}
      {warning > 0 && (
        <span className="flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded-md" style={{ background: 'var(--severity-warning-bg)', color: 'var(--severity-warning)', border: '1px solid rgba(255,190,61,0.2)' }}>
          <AlertTriangle size={10} aria-hidden="true" />
          {warning}
        </span>
      )}
      {pass > 0 && (
        <span className="flex items-center gap-1 text-xs font-mono px-2 py-0.5 rounded-md" style={{ background: 'var(--severity-pass-bg)', color: 'var(--severity-pass)', border: '1px solid rgba(61,255,143,0.15)' }}>
          <CheckCircle2 size={10} aria-hidden="true" />
          {pass}
        </span>
      )}
    </div>
  );
}

export default function PastAuditsPage() {
  const router = useRouter();
  const [scans, setScans] = useState<ScanRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getScanHistory();
      setScans(data);
    } catch (err) {
      console.error('Failed to load scan history:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const handleDelete = useCallback(async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeletingId(id);
    try {
      await deleteScan(id);
      setScans(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.error('Failed to delete scan:', err);
    } finally {
      setDeletingId(null);
    }
  }, []);

  const handleReAudit = useCallback((url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const encoded = encodeURIComponent(url.replace(/^https?:\/\//, ''));
    router.push(`/?url=${encoded}`);
  }, [router]);

  return (
    <div className="min-h-screen bg-background">
      <div
        className="fixed inset-0 pointer-events-none"
        style={{
          backgroundImage: `
            linear-gradient(rgba(255,255,255,0.015) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.015) 1px, transparent 1px)
          `,
          backgroundSize: '40px 40px',
        }}
        aria-hidden="true"
      />
      <div
        className="fixed inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse 80% 50% at 50% -10%, rgba(255,61,87,0.08), transparent)' }}
        aria-hidden="true"
      />

      <Topbar />

      <main className="relative z-10 w-full max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 pt-10">
        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <History size={20} style={{ color: 'var(--primary)' }} aria-hidden="true" />
              <h1 className="text-2xl font-bold text-foreground">Past Audits</h1>
            </div>
            <p className="text-muted-foreground text-sm">
              Track your conversion friction improvements over time
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={loadHistory}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all duration-200 text-sm"
            >
              <RefreshCw size={14} aria-hidden="true" />
              Refresh
            </button>
            <Link
              href="/"
              className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #FF3D57 0%, #C2001E 100%)', color: 'white' }}
            >
              <Search size={14} aria-hidden="true" />
              New Audit
            </Link>
          </div>
        </div>

        {/* Loading state */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Loader2 size={32} className="animate-spin" style={{ color: 'var(--primary)' }} aria-hidden="true" />
            <p className="text-muted-foreground text-sm font-mono">Loading audit history…</p>
          </div>
        )}

        {/* Empty state */}
        {!loading && scans.length === 0 && (
          <div
            className="flex flex-col items-center justify-center py-24 rounded-2xl border"
            style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
          >
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4"
              style={{ background: 'rgba(255,61,87,0.08)', border: '1px solid rgba(255,61,87,0.15)' }}
            >
              <History size={28} style={{ color: 'var(--primary)' }} aria-hidden="true" />
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-2">No audits yet</h2>
            <p className="text-muted-foreground text-sm text-center max-w-xs mb-6">
              Run your first audit to start tracking conversion friction improvements over time.
            </p>
            <Link
              href="/"
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 active:scale-95"
              style={{ background: 'linear-gradient(135deg, #FF3D57 0%, #C2001E 100%)', color: 'white' }}
            >
              <Search size={15} aria-hidden="true" />
              Audit your first URL
            </Link>
          </div>
        )}

        {/* Audit list */}
        {!loading && scans.length > 0 && (
          <div className="space-y-3">
            {scans.map((scan, index) => {
              const scoreColor = getScoreColor(scan.overallScore);
              const cleanUrl = scan.url.replace(/^https?:\/\//, '').replace(/\/$/, '');
              const auditDate = new Date(scan.auditedAt);

              return (
                <div
                  key={scan.id}
                  className="group relative rounded-xl border transition-all duration-200 hover:border-border/80"
                  style={{
                    background: 'var(--card)',
                    borderColor: 'var(--border)',
                    animation: `slideInRow 0.35s cubic-bezier(0.16,1,0.3,1) ${index * 0.04}s both`,
                  }}
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-4 p-4 lg:p-5">
                    {/* Score badge */}
                    <div
                      className="flex-shrink-0 w-14 h-14 rounded-xl flex flex-col items-center justify-center border"
                      style={{
                        background: `${scoreColor}10`,
                        borderColor: `${scoreColor}30`,
                      }}
                    >
                      <span className="text-xl font-bold font-mono tabular-nums leading-none" style={{ color: scoreColor }}>
                        {scan.overallScore}
                      </span>
                      <span className="text-xs font-mono" style={{ color: scoreColor, opacity: 0.7 }}>/100</span>
                    </div>

                    {/* Main content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start gap-2 flex-wrap mb-1.5">
                        <span className="text-sm font-semibold text-foreground font-mono truncate max-w-xs">
                          {cleanUrl}
                        </span>
                        <div
                          className="flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold flex-shrink-0"
                          style={{ background: `${scoreColor}15`, color: scoreColor }}
                        >
                          <ScoreIcon score={scan.overallScore} />
                          {getScoreLabel(scan.overallScore)}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 flex-wrap">
                        <SeverityPills checks={scan.checks} />
                        <span className="flex items-center gap-1 text-xs font-mono text-muted-foreground">
                          <Clock size={10} aria-hidden="true" />
                          {auditDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                          {' · '}
                          {auditDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <a
                        href={scan.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={e => e.stopPropagation()}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:border-foreground/30 transition-all duration-200 text-xs font-medium"
                        aria-label={`Open ${cleanUrl} in new tab`}
                      >
                        <ExternalLink size={12} aria-hidden="true" />
                        <span className="hidden sm:block">Visit</span>
                      </a>
                      <button
                        onClick={e => handleReAudit(scan.url, e)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 active:scale-95"
                        style={{
                          background: 'rgba(255,61,87,0.1)',
                          color: 'var(--severity-critical)',
                          border: '1px solid rgba(255,61,87,0.2)',
                        }}
                        aria-label={`Re-audit ${cleanUrl}`}
                      >
                        <RefreshCw size={12} aria-hidden="true" />
                        Re-audit
                      </button>
                      <button
                        onClick={e => handleDelete(scan.id, e)}
                        disabled={deletingId === scan.id}
                        className="flex items-center justify-center w-8 h-8 rounded-lg border border-border text-muted-foreground hover:text-red-400 hover:border-red-400/30 transition-all duration-200 disabled:opacity-50"
                        aria-label={`Delete audit for ${cleanUrl}`}
                      >
                        {deletingId === scan.id
                          ? <Loader2 size={13} className="animate-spin" aria-hidden="true" />
                          : <Trash2 size={13} aria-hidden="true" />
                        }
                      </button>
                    </div>
                  </div>

                  {/* Score bar */}
                  <div className="px-4 lg:px-5 pb-4">
                    <div className="h-1 rounded-full overflow-hidden" style={{ background: 'var(--muted)' }}>
                      <div
                        className="h-full rounded-full transition-all duration-700"
                        style={{ width: `${scan.overallScore}%`, background: scoreColor }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Summary stats */}
        {!loading && scans.length > 1 && (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              {
                label: 'Total Audits',
                value: scans.length,
                color: 'var(--foreground)',
              },
              {
                label: 'Average Score',
                value: Math.round(scans.reduce((s, r) => s + r.overallScore, 0) / scans.length),
                color: getScoreColor(Math.round(scans.reduce((s, r) => s + r.overallScore, 0) / scans.length)),
                suffix: '/100',
              },
              {
                label: 'Best Score',
                value: Math.max(...scans.map(s => s.overallScore)),
                color: getScoreColor(Math.max(...scans.map(s => s.overallScore))),
                suffix: '/100',
              },
            ].map(stat => (
              <div
                key={stat.label}
                className="flex flex-col items-center p-5 rounded-xl border"
                style={{ background: 'var(--card)', borderColor: 'var(--border)' }}
              >
                <span className="text-3xl font-bold font-mono tabular-nums" style={{ color: stat.color }}>
                  {stat.value}{stat.suffix || ''}
                </span>
                <span className="text-xs font-mono text-muted-foreground mt-1">{stat.label}</span>
              </div>
            ))}
          </div>
        )}
      </main>

      <footer className="relative z-10 border-t border-border py-6 mt-8">
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-muted-foreground text-sm">FrictionScan — Heuristic conversion audit for SaaS landing pages</p>
          <p className="text-muted-foreground text-xs font-mono">v2.0 · 10 signals · PageSpeed Insights</p>
        </div>
      </footer>
    </div>
  );
}
